import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import Modal from "../components/Modal";

// Reusable form input component
const FormInput = ({
  label,
  id,
  name,
  type = "text",
  value,
  onChange,
  required = false,
  disabled = false,
}) => {
  return (
    <div className="form-group">
      <label htmlFor={id} className="form-label">
        {label}
      </label>
      <input
        id={id}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        className="form-control"
        required={required}
        disabled={disabled}
      />
    </div>
  );
};

// Investment form row component
const InvestmentRow = ({ index, investment, onChange }) => {
  return (
    <div className="input-row mb-4">
      <FormInput
        label="Amount (₹)"
        id={`amount-${index}`}
        name={`investments.${index}.amount`}
        type="number"
        value={investment.amount}
        onChange={onChange}
        required
      />
      <FormInput
        label="Interest Rate (%)"
        id={`interestRate-${index}`}
        name={`investments.${index}.interestRate`}
        type="number"
        value={investment.interestRate}
        onChange={onChange}
        required
      />
      <div className="form-group">
        <label htmlFor={`durationValue-${index}`} className="form-label">
          Duration
        </label>
        <div className="flex gap-2">
          <input
            id={`durationValue-${index}`}
            name={`investments.${index}.durationValue`}
            type="number"
            value={investment.durationValue}
            onChange={onChange}
            className="form-control flex-grow"
            required
          />
          <select
            id={`durationUnit-${index}`}
            name={`investments.${index}.durationUnit`}
            value={investment.durationUnit}
            onChange={onChange}
            className="form-control w-32"
          >
            <option value="months">Months</option>
            <option value="years">Years</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// Document upload component
const DocumentUploader = ({
  label,
  id,
  onChange,
  files,
  onRemove,
  error,
  disabled,
}) => {
  return (
    <div className="form-group col-span-2">
      <label className="form-label">{label}</label>
      <div className="flex flex-col gap-4">
        <label htmlFor={id} className="btn btn-outline btn-sm">
          Upload Document Images
        </label>
        <input
          name={id}
          type="file"
          onChange={onChange}
          className="hidden"
          id={id}
          disabled={disabled}
          multiple
        />
        {error && <div className="text-red-500">{error}</div>}
        <div className="document-gallery">
          {files.length > 0 ? (
            files.map((file, index) => (
              <div key={index} className="document-selected">
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Document ${index + 1}`}
                  className="w-full h-32 object-cover rounded border"
                />
                <button
                  onClick={() => onRemove(index)}
                  className="btn btn-sm-close"
                  type="button"
                >
                  ×
                </button>
                <div className="text-xs mt-1 truncate">{file.name}</div>
              </div>
            ))
          ) : (
            <div className="text-gray-400">No documents selected</div>
          )}
        </div>
      </div>
    </div>
  );
};

const AddClient = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    dob: "",
    panNumber: "",
    password: "",
    profilePicture: "",
    documentImages: [],
    bankDetails: { bankName: "", accountNumber: "", ifscCode: "" },
    investments: [
      {
        amount: "",
        interestRate: "",
        durationValue: "",
        durationUnit: "months",
      },
    ],
    hasReferrer: false,
    referrerName: "",
    referrerContact: "",
    hasNominee: false,
    nomineeName: "",
    nomineeContact: "",
    nomineeRelation: "",
  });

  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [nomineeDocumentFiles, setNomineeDocumentFiles] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const handleCancelAdding = () => {
    navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkboxes
    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked,
      });
      return;
    }

    // Fields that need to be uppercased
    const upperCaseFields = ["panNumber", "bankDetails.ifscCode"];

    // Fields that need to be capitalized
    const capitalizeFields = ["name", "nomineeName", "referrerName"];

    // Helper function to capitalize each word in a string
    const capitalizeName = (name) => {
      return name
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
    };

    // Normalize function to handle uppercase and capitalization
    const normalize = (fieldName, fieldValue) => {
      if (upperCaseFields.includes(fieldName)) {
        return fieldValue.toUpperCase();
      } else if (capitalizeFields.includes(fieldName)) {
        return capitalizeName(fieldValue);
      } else {
        return fieldValue;
      }
    };

    if (name.startsWith("bankDetails.")) {
      const field = name.split(".")[1];
      const fieldPath = `bankDetails.${field}`;
      setFormData({
        ...formData,
        bankDetails: {
          ...formData.bankDetails,
          [field]: normalize(fieldPath, value),
        },
      });
    } else if (name.startsWith("investments.")) {
      const [, index, field] = name.match(/investments\.(\d+)\.(\w+)/);
      const newInvestments = [...formData.investments];
      if (field === "durationUnit") {
        const oldUnit = newInvestments[index].durationUnit;
        const newUnit = value;
        const oldValue = newInvestments[index].durationValue;
        let newValue = oldValue;
        if (oldValue && !isNaN(parseFloat(oldValue))) {
          if (oldUnit === "months" && newUnit === "years") {
            newValue = (parseFloat(oldValue) / 12).toString();
          } else if (oldUnit === "years" && newUnit === "months") {
            newValue = (parseFloat(oldValue) * 12).toString();
          }
        }
        newInvestments[index].durationValue = newValue;
        newInvestments[index].durationUnit = newUnit;
      } else {
        newInvestments[index][field] = value;
      }
      setFormData({ ...formData, investments: newInvestments });
    } else {
      setFormData({ ...formData, [name]: normalize(name, value) });
    }
  };

  const addInvestment = () => {
    setFormData({
      ...formData,
      investments: [
        ...formData.investments,
        {
          amount: "",
          interestRate: "",
          durationValue: "",
          durationUnit: "months",
        },
      ],
    });
  };

  const handleConfirmAdding = async () => {
    setIsProcessing(true);
    setShowConfirmModal(false);
    try {
      // build multipart FormData
      const data = new FormData();
      data.append("name", formData.name);
      data.append("email", formData.email);
      data.append("phone", formData.phone);
      data.append("dob", formData.dob);
      data.append("panNumber", formData.panNumber);
      data.append("password", formData.password);

      // Referrer information
      data.append("hasReferrer", formData.hasReferrer);
      if (formData.hasReferrer) {
        data.append("referrerName", formData.referrerName);
        data.append("referrerContact", formData.referrerContact);
      }

      // Nominee information
      data.append("hasNominee", formData.hasNominee);
      if (formData.hasNominee) {
        data.append("nomineeName", formData.nomineeName);
        data.append("nomineeContact", formData.nomineeContact);
        data.append("nomineeRelation", formData.nomineeRelation);
      }

      data.append("bankDetails", JSON.stringify(formData.bankDetails));

      const invs = formData.investments.map((inv) => ({
        amount: parseFloat(inv.amount),
        interestRate: parseFloat(inv.interestRate),
        duration:
          inv.durationUnit === "years"
            ? parseFloat(inv.durationValue) * 12
            : parseFloat(inv.durationValue),
      }));
      data.append("investments", JSON.stringify(invs));

      if (selectedFile) data.append("profilePicture", selectedFile);

      // Append each document file separately
      documentFiles.forEach((file) => {
        data.append("documentImages", file);
      });

      // Append nominee documents if applicable
      if (formData.hasNominee) {
        nomineeDocumentFiles.forEach((file) => {
          data.append("nomineeDocumentImages", file);
        });
      }

      // post as multipart/form-data (let browser set boundary)
      await api.post("/admin/clients", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to add client");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
    }
  };

  const handleDocumentChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      // Check if adding these files would exceed the limit of 5
      if (documentFiles.length + files.length > 5) {
        setError("Maximum 5 documents allowed");
        return;
      }
      setDocumentFiles((prevFiles) => [...prevFiles, ...files]);
      setError(""); // Clear any error
    }
  };

  const handleNomineeDocumentChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      // Check if adding these files would exceed the limit of 5
      if (nomineeDocumentFiles.length + files.length > 5) {
        setError("Maximum 5 nominee documents allowed");
        return;
      }
      setNomineeDocumentFiles((prevFiles) => [...prevFiles, ...files]);
      setError(""); // Clear any error
    }
  };

  // Function to remove a selected document
  const removeDocument = (index) => {
    setDocumentFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // Function to remove a selected nominee document
  const removeNomineeDocument = (index) => {
    setNomineeDocumentFiles((prevFiles) =>
      prevFiles.filter((_, i) => i !== index)
    );
  };

  return (
    <div className="card main-content max-w-xl">
      <h2 className="text-2xl font-semibold mb-6">Add New Client</h2>
      {error && <div className="alert alert-danger mb-6">{error}</div>}
      <form onSubmit={handleSubmit}>
        {/* Personal Details */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Personal Details</h3>
          <div className="input-row">
            <div className="form-group col-span-2">
              <div className="flex items-center gap-2">
                <img
                  src={
                    selectedFile
                      ? URL.createObjectURL(selectedFile)
                      : "/default.png"
                  }
                  alt="Profile"
                  className="profile-pic"
                />
                <label
                  htmlFor="profilePicture"
                  className="btn btn-outline btn-sm"
                >
                  Upload Image
                </label>
                <input
                  name="profilePicture"
                  type="file"
                  onChange={handleFileChange}
                  className="hidden"
                  id="profilePicture"
                  disabled={isProcessing}
                />
              </div>
            </div>
            <FormInput
              label="Name"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <FormInput
              label="Email"
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            <FormInput
              label="Phone"
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
            />
            <FormInput
              label="Date of Birth"
              id="dob"
              name="dob"
              type="date"
              value={formData.dob}
              onChange={handleChange}
              required
            />
            <FormInput
              label="PAN Number"
              id="panNumber"
              name="panNumber"
              value={formData.panNumber}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* Bank Details */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Bank Details</h3>
          <div className="input-row">
            <FormInput
              label="Bank Name"
              id="bankName"
              name="bankDetails.bankName"
              value={formData.bankDetails.bankName}
              onChange={handleChange}
              required
            />
            <FormInput
              label="Account Number"
              id="accountNumber"
              name="bankDetails.accountNumber"
              value={formData.bankDetails.accountNumber}
              onChange={handleChange}
              required
            />
            <FormInput
              label="IFSC Code"
              id="ifscCode"
              name="bankDetails.ifscCode"
              value={formData.bankDetails.ifscCode}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        {/* Investments */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">Investments</h3>
          {formData.investments.map((inv, index) => (
            <InvestmentRow
              key={index}
              index={index}
              investment={inv}
              onChange={handleChange}
            />
          ))}
          <button
            type="button"
            onClick={addInvestment}
            className="btn btn-primary btn-sm mb-3"
          >
            Add Another Investment
          </button>
        </div>

        {/* Documents */}
        <DocumentUploader
          label="Upload Documents (Max 5)"
          id="documentImages"
          onChange={handleDocumentChange}
          files={documentFiles}
          onRemove={removeDocument}
          error={error}
          disabled={isProcessing}
        />

        {/* Password */}
        <div className="input-row">
          <FormInput
            label="Password"
            id="password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>

        {/* Referrer Section */}
        <div className="mb-8">
          <div className="form-group">
            <div className="flex items-center gap-2">
              <input
                id="hasReferrer"
                name="hasReferrer"
                type="checkbox"
                checked={formData.hasReferrer}
                onChange={handleChange}
                className="form-checkbox"
              />
              <label htmlFor="hasReferrer" className="form-label">
                Does this client have a referrer?
              </label>
            </div>
          </div>

          {formData.hasReferrer && (
            <div className="input-row">
              <FormInput
                label="Referrer Name"
                id="referrerName"
                name="referrerName"
                type="text"
                value={formData.referrerName}
                onChange={handleChange}
                required={formData.hasReferrer}
              />
              <FormInput
                label="Referrer Contact"
                id="referrerContact"
                name="referrerContact"
                type="tel"
                value={formData.referrerContact}
                onChange={handleChange}
                required={formData.hasReferrer}
              />
            </div>
          )}
        </div>

        {/* Nominee Section */}
        <div className="mb-8">
          <div className="form-group">
            <div className="flex items-center gap-2">
              <input
                id="hasNominee"
                name="hasNominee"
                type="checkbox"
                checked={formData.hasNominee}
                onChange={handleChange}
                className="form-checkbox"
              />
              <label htmlFor="hasNominee" className="form-label">
                Does this client have a nominee?
              </label>
            </div>
          </div>

          {formData.hasNominee && (
            <>
              <div className="input-row">
                <FormInput
                  label="Nominee Name"
                  id="nomineeName"
                  name="nomineeName"
                  type="text"
                  value={formData.nomineeName}
                  onChange={handleChange}
                  required={formData.hasNominee}
                />
                <FormInput
                  label="Nominee Contact"
                  id="nomineeContact"
                  name="nomineeContact"
                  type="tel"
                  value={formData.nomineeContact}
                  onChange={handleChange}
                  required={formData.hasNominee}
                />
                <FormInput
                  label="Relation with Nominee"
                  id="nomineeRelation"
                  name="nomineeRelation"
                  type="text"
                  value={formData.nomineeRelation}
                  onChange={handleChange}
                  required={formData.hasNominee}
                />
              </div>

              <DocumentUploader
                label="Upload Nominee Documents (Max 5)"
                id="nomineeDocumentImages"
                onChange={handleNomineeDocumentChange}
                files={nomineeDocumentFiles}
                onRemove={removeNomineeDocument}
                error={error}
                disabled={isProcessing}
              />
            </>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate("/admin/dashboard")}
            className="btn btn-outline btn-sm"
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary btn-sm">
            Add Client
          </button>
        </div>
      </form>

      {/* Confirmation Modal */}
      <Modal
        isOpen={showConfirmModal}
        onClose={handleCancelAdding}
        title="Add New Client"
        buttons={[
          {
            text: "No",
            onClick: handleCancelAdding,
            color: "outline",
            disabled: isProcessing,
          },
          {
            text: "Yes, Add",
            onClick: handleConfirmAdding,
            color: "success",
            disabled: isProcessing,
          },
        ]}
      >
        <p>Are you sure you want to add this new client's information?</p>
      </Modal>

      {/* Processing Modal */}
      <Modal
        isOpen={isProcessing}
        showSpinner={true}
        spinnerText="Adding Client Information"
        spinnerSubText="Please do not close this window or your device. You will be redirected once the client is added in the system."
        size="md"
      />
    </div>
  );
};

export default AddClient;
